import {
  ASSET_CREATE_MAX_IMAGE_FILE_BYTES,
  ASSET_IMAGE_COMPRESS_BEFORE_UPLOAD,
  ASSET_UPLOAD_MAX_EDGE,
  ASSET_UPLOAD_TARGET_MAX_BYTES,
} from '../constants/assetCreateUpload';
import { assertAssetCreateImageFilesWithinLimits, type AssetCreateImageLimitMessages } from './assetCreateImageValidate';

function jpegOutputName(originalName: string): string {
  const dot = originalName.lastIndexOf('.');
  const base = dot > 0 ? originalName.slice(0, dot) : originalName;
  return `${base}.jpg`;
}

function canvasToJpegBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
  });
}

/**
 * Raster images (JPEG/PNG/WebP/…) are resized (max edge) and re-encoded as JPEG toward
 * {@link ASSET_UPLOAD_TARGET_MAX_BYTES}. SVG/GIF and non-images are returned unchanged.
 */
async function compressSingleFile(file: File): Promise<File> {
  if (!ASSET_IMAGE_COMPRESS_BEFORE_UPLOAD) {
    return file;
  }
  const t = file.type.toLowerCase();
  if (!t.startsWith('image/') || t === 'image/svg+xml' || t === 'image/gif') {
    return file;
  }
  if (
    typeof createImageBitmap !== 'function' ||
    typeof document === 'undefined' ||
    typeof document.createElement !== 'function'
  ) {
    return file;
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }

  const maxEdge = ASSET_UPLOAD_MAX_EDGE;
  const targetBytes = ASSET_UPLOAD_TARGET_MAX_BYTES;
  let w = bitmap.width;
  let h = bitmap.height;
  if (w < 1 || h < 1) {
    bitmap.close();
    return file;
  }
  const scaleFit = Math.min(1, maxEdge / Math.max(w, h));
  w = Math.max(1, Math.round(w * scaleFit));
  h = Math.max(1, Math.round(h * scaleFit));

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    return file;
  }

  let bestBlob: Blob | null = null;
  let bestSize = Infinity;

  try {
    for (let shrink = 0; shrink < 10; shrink++) {
      canvas.width = w;
      canvas.height = h;
      ctx.drawImage(bitmap, 0, 0, w, h);

      for (let q = 0.92; q >= 0.4; q -= 0.07) {
        const blob = await canvasToJpegBlob(canvas, q);
        if (!blob) {
          continue;
        }
        if (blob.size < bestSize) {
          bestBlob = blob;
          bestSize = blob.size;
        }
        if (blob.size <= targetBytes && blob.size <= ASSET_CREATE_MAX_IMAGE_FILE_BYTES) {
          bitmap.close();
          return new File([blob], jpegOutputName(file.name), {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });
        }
      }

      w = Math.max(48, Math.round(w * 0.82));
      h = Math.max(48, Math.round(h * 0.82));
    }

    bitmap.close();

    if (bestBlob !== null && bestBlob.size <= ASSET_CREATE_MAX_IMAGE_FILE_BYTES) {
      return new File([bestBlob], jpegOutputName(file.name), {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
    }

    return file;
  } catch {
    bitmap.close();
    return file;
  }
}

/** Before multipart POST/PATCH asset calls, shrink raster images to reduce upload time and timeouts. */
export async function compressImageFilesForAssetUpload(files: readonly File[]): Promise<File[]> {
  if (files.length === 0) {
    return [];
  }
  if (!ASSET_IMAGE_COMPRESS_BEFORE_UPLOAD) {
    return [...files];
  }
  const out: File[] = [];
  for (const f of files) {
    out.push(await compressSingleFile(f));
  }
  return out;
}

/** Same pipeline for **Create** and **Update** multipart: compress then enforce server size/file-count limits. */
export async function prepareImageFilesForMultipartUpload(
  files: readonly File[],
  limitMessages?: AssetCreateImageLimitMessages,
): Promise<File[]> {
  const ready = await compressImageFilesForAssetUpload(files);
  assertAssetCreateImageFilesWithinLimits(ready, limitMessages);
  return ready;
}
